import { useState, useEffect } from 'react';
import { HelpCircleIcon, Instagram, Linkedin, Twitter, ArrowRight, Facebook } from 'lucide-react'
import { PageHeader } from "@/components/ui/page-header"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { InstagramIntegrationGuide } from './InstagramIntegrationGuide';
import { FacebookIntegrationGuide } from './FacebookIntegrationGuide';

type HelpView = 'main' | 'instagram-guide' | 'facebook-guide';

export default function Help() {
    const [currentView, setCurrentView] = useState<HelpView>('main');

    // Check for URL fragment on component mount
    useEffect(() => {
        const hash = window.location.hash.substring(1); // Remove the # symbol
        if (hash === 'instagram-guide') {
            setCurrentView('instagram-guide');
        } else if (hash === 'facebook-guide') {
            setCurrentView('facebook-guide');
        }
    }, []);

    // Handle navigation back to main view
    const handleBackToMain = () => {
        setCurrentView('main');
        // Update URL to remove hash
        window.history.pushState({}, '', window.location.pathname);
    };

    // Handle navigation to Instagram guide
    const handleShowInstagramGuide = () => {
        setCurrentView('instagram-guide');
        // Update URL with hash
        window.history.pushState({}, '', window.location.pathname + '#instagram-guide');
    };

    // Handle navigation to Facebook guide
    const handleShowFacebookGuide = () => {
        setCurrentView('facebook-guide');
        // Update URL with hash
        window.history.pushState({}, '', window.location.pathname + '#facebook-guide');
    };

    if (currentView === 'instagram-guide') {
        return <InstagramIntegrationGuide onBack={handleBackToMain} />;
    }

    if (currentView === 'facebook-guide') {
        return <FacebookIntegrationGuide onBack={handleBackToMain} />;
    }

    return (
        <div className="p-6 space-y-8">
            <PageHeader
                title="Help & Support"
                description="Get help with using Rainfactory AI and find answers to common questions."
                icon={<HelpCircleIcon className="h-6 w-6" />}
            />
            
            {/* Integration Guides Section */}
            <div className="space-y-4">
                <h2 className="text-2xl font-semibold">Integration Guides</h2>
                <p className="text-muted-foreground">Step-by-step guides to connect your social media accounts with Rainfactory.</p>
                
                <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                    {/* Instagram Guide */}
                    <Card className="cursor-pointer hover:shadow-md transition-shadow">
                        <CardHeader className="pb-3">
                            <CardTitle className="flex items-center gap-3">
                                <div className="h-8 w-8 rounded-full bg-gradient-to-br from-pink-500 via-red-500 to-yellow-500 flex items-center justify-center">
                                    <Instagram className="h-4 w-4 text-white" />
                                </div>
                                Instagram Business
                            </CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            <p className="text-sm text-muted-foreground">
                                Connect your Instagram Business account to schedule and publish posts automatically.
                            </p>
                            <Button 
                                variant="outline" 
                                className="w-full justify-between"
                                onClick={handleShowInstagramGuide}
                            >
                                View Guide
                                <ArrowRight className="h-4 w-4" />
                            </Button>
                        </CardContent>
                    </Card>

                    {/* Facebook Guide */}
                    <Card className="cursor-pointer hover:shadow-md transition-shadow">
                        <CardHeader className="pb-3">
                            <CardTitle className="flex items-center gap-3">
                                <div className="h-8 w-8 rounded-full bg-blue-600 flex items-center justify-center">
                                    <Facebook className="h-4 w-4 text-white" />
                                </div>
                                Facebook Business
                            </CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            <p className="text-sm text-muted-foreground">
                                Connect your Facebook Business Page to schedule and publish posts directly.
                            </p>
                            <Button 
                                variant="outline" 
                                className="w-full justify-between"
                                onClick={handleShowFacebookGuide}
                            >
                                View Guide
                                <ArrowRight className="h-4 w-4" />
                            </Button>
                        </CardContent>
                    </Card>

                    {/* LinkedIn Guide - Coming Soon */}
                    <Card className="opacity-60">
                        <CardHeader className="pb-3">
                            <CardTitle className="flex items-center gap-3">
                                <div className="h-8 w-8 rounded-full bg-blue-600 flex items-center justify-center">
                                    <Linkedin className="h-4 w-4 text-white" />
                                </div>
                                LinkedIn
                            </CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            <p className="text-sm text-muted-foreground">
                                Connect your LinkedIn profile and company pages.
                            </p>
                            <Button variant="outline" className="w-full" disabled>
                                Coming Soon
                            </Button>
                        </CardContent>
                    </Card>

                    {/* Twitter/X Guide - Coming Soon */}
                    <Card className="opacity-60">
                        <CardHeader className="pb-3">
                            <CardTitle className="flex items-center gap-3">
                                <div className="h-8 w-8 rounded-full bg-black flex items-center justify-center">
                                    <Twitter className="h-4 w-4 text-white" />
                                </div>
                                X (Twitter)
                            </CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            <p className="text-sm text-muted-foreground">
                                Connect your X account to share content and engage with your audience.
                            </p>
                            <Button variant="outline" className="w-full" disabled>
                                Coming Soon
                            </Button>
                        </CardContent>
                    </Card>
                </div>
            </div>

        </div>
    )
} 